import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
// import { Link as RouterLink } from 'react-router-dom' Uncomment when in use
import Box from '@mui/material/Box'
import { useQuery } from '@tanstack/react-query'

import TicketAPI from '../../api/orbitV1/TicketAPI'
import StatContainer from '../../common/AbstractStatLine/StatContainer'
import PageLayout from '../../common/Layout/Page/PageLayout'
import { ERR_UNEXPECTED } from '../../constants/errors'
import JoyrideContext from '../../context/joyride/joyrideContext'
import OrbitSession from '../../hooks/useCookies'
import useOrbitFlag from '../../hooks/useOrbitFlag'

import SubmitRequest from './components/SubmitRequest'
import SupportTickets from './components/SupportTickets'
import { TicketDrawer } from './components/UpdateTickets'

export default function CustomerSupportTickets() {
  const [unexpectedError, setUnexpectedError] = useState('')
  const joyrideContext = useContext(JoyrideContext)
  const { tourActive, setRun } = joyrideContext

  /* get current user info */
  const role = OrbitSession.get('role')
  const userId = OrbitSession.get('_id') // in future, userId will be used to Get all relevant info from DB
  const userName = OrbitSession.get('userName')
  const orgId = OrbitSession.get('orgId')
  const orgName = OrbitSession.get('orgName')
  const userEmail = OrbitSession.get('email')

  const TICKETS_LIMIT = 50
  const [tickets, setTickets] = useState([])
  const [page, setPage] = useState(0)
  const [dataArray, setDataArray] = useState([])
  const FlagStatContainer = useOrbitFlag('statContainer')
  /* Handle states for viewing, replying to & updating tickets */
  const [ticketDetails, setTicketDetails] = useState({})
  const [replyMessage, setReplyMessage] = useState('')

  const welcomeMessage = 'Hello Circles Life...'

  /* Handle states for submitting requests */
  const [subjectOptions, setSubjectOptions] = useState({})
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketMessage, setTicketMessage] = useState(welcomeMessage)
  const [ticketAttachment, setTicketAttachment] = useState('')

  /* handle view and reply drawer */
  const [isDrawerOpen, setIsDrawerOpen] = useState({
    right: false
  })

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }

    setIsDrawerOpen({ ...isDrawerOpen, [anchor]: open })
  }

  useEffect(() => {
    getTicketTypes()
  }, [])

  const getTicketTypes = async () => {
    try {
      const ticketTypes = await TicketAPI.getTicketTypes()
      setSubjectOptions(ticketTypes.data)
      setTicketSubject(Object.keys(ticketTypes.data)[0])
    } catch (error) {
      toast.error('Error loading ticket types!')
    }
  }

  const {
    status: retrieveTicketsStatus,
    data: retrieveTickets,
    error: retrieveTicketsError,
    refetch
  } = useQuery(
    ['getTickets', userId],
    async () => {
      if (['opsowner', 'ops'].includes(role)) {
        const { data } = await TicketAPI.getTickets()
        return data
      } else {
        const { data } = await TicketAPI.getTickets(
          TICKETS_LIMIT,
          JSON.stringify({
            'clientInfo.userId': userId
          })
        )
        return data
      }
    },
    { refetchInterval: 10000 }
  )

  useEffect(() => {
    if (retrieveTickets) {
      const allTickets = retrieveTickets

      const openTickets = allTickets.filter((t) => t.status === 'Open')

      const resolvedTickets = allTickets.filter((t) => t.status === 'Resolved')

      const todayDate = new Date().getDate()

      const isExpired = (ticket) => {
        if (ticket.replies.length > 0) {
          const latestReply = ticket.replies[ticket.replies.length - 1]
          const dateDiff = new Date(latestReply.repliedAt).getDate() - todayDate
          return latestReply.authorId !== userId && dateDiff >= 3
        }
      }

      const expiredTickets = allTickets.filter(isExpired)

      setTickets(retrieveTickets)
      setDataArray([
        { title: 'Total tickets', info: allTickets.length },
        { title: 'Open Tickets', info: openTickets.length },
        { title: 'Resolved Tickets', info: resolvedTickets.length },
        { title: '> 3 days', info: expiredTickets.length }
      ])
      if (tourActive) {
        setTimeout(() => {
          setRun(true)
        }, 1000)
      }
    }
  }, [retrieveTickets])

  if (retrieveTicketsError) {
    toast.error('Error retrieving tickets. Please try again later!')
  }

  const handleCreateTicket = async () => {
    try {
      const ticketCreated = await TicketAPI.createTicket({
        orgId,
        orgName,
        userId,
        userName,
        userEmail,
        subject: ticketSubject,
        message: ticketMessage,
        attachmentURL: ticketAttachment
      })

      if (ticketCreated.data) {
        toast.success('Support ticket created!', {
          autoClose: 3000,
          hideProgressBar: false
        })
        setTimeout(() => {
          // reset params
          setTicketSubject(Object.keys(subjectOptions)[0])
          setTicketMessage(welcomeMessage)
          goPreviousPage()
        }, 2000)
      } else {
        toast.error(ERR_UNEXPECTED)
      }
    } catch (error) {
      setUnexpectedError(ERR_UNEXPECTED)
    }
  }

  const handleUpdateTicket = async (action) => {
    // compose new reply
    let respMessage
    const newReply = {
      authorId: userId,
      author: userName,
      message: replyMessage,
      repliedAt: new Date()
    }
    // define resolved status
    let newStatus
    let closedAt
    let lastReplyAt

    // check if update is to close or reply to ticket
    if (action === 'reply') {
      ticketDetails.replies.push(newReply)
      closedAt = ''
      newStatus = ticketDetails.status
      lastReplyAt = newReply.repliedAt
      respMessage = 'Ticket Updated!'
    }

    if (action === 'resolve') {
      closedAt = new Date()
      lastReplyAt = closedAt
      newStatus = 'Resolved'
      respMessage = 'Ticket resolved!'
    }

    try {
      const ticketUpdated = await TicketAPI.updateTicket({
        id: ticketDetails.id,
        userEmail: ticketDetails.userEmail,
        userName: ticketDetails.userName,
        customerId: ticketDetails.userId,
        ticketNo: ticketDetails.ticketNo,
        replies: ticketDetails.replies,
        authorId: newReply.authorId,
        status: newStatus,
        priority: ticketDetails.priority,
        assigneeInfo: ticketDetails.assigneeInfo,
        lastReplyAt,
        closedAt,
        isNewTicket: false
      })
      if (ticketUpdated) {
        toast.success(respMessage, {
          autoClose: 3000,
          hideProgressBar: false
        })
        refetch()
        setTimeout(() => {
          // reset params
          setTicketSubject(Object.keys(subjectOptions)[0])
          setTicketMessage(welcomeMessage)
          setReplyMessage('')
        }, 2000)
        if (action === 'resolve') {
          setTimeout(() => {
            setIsDrawerOpen({ right: false })
          }, 2000)
        }
      } else {
        toast.error(ERR_UNEXPECTED)
      }
    } catch (error) {
      setUnexpectedError(ERR_UNEXPECTED)
    }
  }

  const goPreviousPage = () => setPage(page - 1)

  const StatContainerInBox = () => (
    <Box marginY='20px'>
      <StatContainer dataArray={dataArray} />
    </Box>
  )

  return (
    <PageLayout header='Customer Support'>
      {/* Fix misalignment for color boxes */}
      {role === 'ops' && FlagStatContainer && <StatContainerInBox />}
      <div id='customerSupport__mainDiv'>
        <Box
          display='flex'
          flexDirection='column'
          width='100%'
          justifyContent='left'
        >
          {
            {
              0: (
                <SupportTickets
                  tickets={tickets}
                  role={role}
                  toggleDrawer={toggleDrawer}
                  currUser={userId}
                  setPage={setPage}
                  retrieveTicketsStatus={retrieveTicketsStatus}
                  setTicketDetails={setTicketDetails}
                />
              ),
              1: (
                <SubmitRequest
                  userName={userName}
                  userEmail={userEmail}
                  subjectOptions={subjectOptions}
                  ticketSubject={ticketSubject}
                  setTicketSubject={setTicketSubject}
                  ticketMessage={ticketMessage}
                  setTicketMessage={setTicketMessage}
                  setTicketAttachment={setTicketAttachment}
                  handleCreateTicket={handleCreateTicket}
                  unexpectedError={unexpectedError}
                  goPreviousPage={goPreviousPage}
                />
              )
            }[page]
          }
        </Box>
      </div>
      <TicketDrawer
        toggleDrawer={toggleDrawer}
        isDrawerOpen={isDrawerOpen}
        ticketDetails={ticketDetails}
        tickets={tickets}
        replyMessage={replyMessage}
        setReplyMessage={setReplyMessage}
        handleUpdateTicket={handleUpdateTicket}
        role={role}
      />
    </PageLayout>
  )
}
